import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus } from 'react-icons/fi';
import { usePermissions } from '../context/PermissionContext';

const MAX_PINS = 5;

// Ids already covered by their own dedicated pinned buttons (Start/Stop Production,
// Orders, QA Review) — excluded from the picker since pinning them again would be redundant.
const EXCLUDED_IDS = new Set(['qa-queue', 'admin-orders', 'factory-orders']);

// User-customizable topbar shortcuts, desktop only. Sits between the QA Review button
// and the Clock/Messaging/Account cluster. Persisted server-side via PermissionContext
// so the picks follow the user across devices.
export default function PinnedQuickActions({ accessibleItems, badgeValues }) {
  const { pinnedQuickActions, setPinnedQuickActions } = usePermissions();
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const pickableItems = useMemo(() => {
    return accessibleItems
      .filter(i => i.path && !i.searchOnly && !EXCLUDED_IDS.has(i.id))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [accessibleItems]);

  const pinnedItems = pinnedQuickActions
    .map(id => pickableItems.find(i => i.id === id))
    .filter(Boolean);

  const toggle = (id) => {
    if (pinnedQuickActions.includes(id)) {
      setPinnedQuickActions(pinnedQuickActions.filter(x => x !== id));
    } else if (pinnedQuickActions.length < MAX_PINS) {
      setPinnedQuickActions([...pinnedQuickActions, id]);
    }
  };

  return (
    <>
      {pinnedItems.map(item => {
        const Icon = item.icon;
        const count = item.badge ? badgeValues[item.badge] : 0;
        return (
          <Link
            key={item.id}
            className="btn btn-sm btn-outline-light position-relative"
            to={item.path}
            title={item.label}
          >
            {Icon && <Icon size={14} />}
            {count > 0 && (
              <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: 10 }}>
                {count > 99 ? '99+' : count}
              </span>
            )}
          </Link>
        );
      })}

      <div className="position-relative" ref={ref}>
        <button
          className="btn btn-sm btn-outline-light d-flex align-items-center"
          onClick={() => setOpen(v => !v)}
          title="Customize quick shortcuts"
        >
          <FiPlus size={14} />
        </button>
        {open && (
          <div className="dropdown-menu show" style={{ position: 'absolute', left: 0, top: '110%', minWidth: 260, maxHeight: 340, overflowY: 'auto', zIndex: 1100 }}>
            <div className="dropdown-item-text" style={{ fontSize: 12, fontWeight: 600 }}>
              Quick shortcuts ({pinnedQuickActions.length}/{MAX_PINS})
            </div>
            <div className="dropdown-divider" />
            {pickableItems.map(item => {
              const Icon = item.icon;
              const checked = pinnedQuickActions.includes(item.id);
              const disabled = !checked && pinnedQuickActions.length >= MAX_PINS;
              return (
                <label
                  key={item.id}
                  className="dropdown-item d-flex align-items-center gap-2"
                  style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}
                >
                  <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggle(item.id)} />
                  {Icon && <Icon size={14} />}
                  <span style={{ fontSize: 13 }}>{item.label}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
